import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import SidebarLayout from '@/components/layout/SidebarLayout';
import { ExternalLink } from 'lucide-react';
import DashboardCard from '@/components/ui/DashboardCard';
import WorkbookHeader from '../WorkbookHeader';
import { TeamMembersSidebar } from '../sidebar/TeamMembersSidebar';
import { usePageMutations } from '@/hooks/content-generator/pages';
import { useWorkbookGoal } from '@/hooks/goal-planner/use-goal-workbook-junction';
import { Button } from '@/components/ui/button';
import GoalSettingsContainer from '@/components/goal-planner/GoalSettingsContainer';
import ProjectSettingsContainer from '@/components/project/ProjectSettingsContainer';
import CompanyInformationContainer from '@/components/project/CompanyInformationContainer';
import { getTabSettings } from '@/hooks/project/use-project-settings';


interface WorkbookSettingsTabProps {
  pageId: string;
  title: string;
  isArchived: boolean;
  coverImage?: string;
  createdAt: string;
  updatedAt: string;
  onToggleArchive: () => void;
  onDelete: () => void;
  onCoverImageChange: (url: string | undefined) => void;
  onCreateTag?: () => void;
  // Loading states for button feedback
  isArchiveToggling?: boolean;
  isDeleting?: boolean;
  // Ownership props
  isOwner?: boolean;
  isCheckingOwnership?: boolean;
}

const WorkbookSettingsTab: React.FC<WorkbookSettingsTabProps> = ({
  pageId,
  title,
  isArchived,
  coverImage,
  createdAt,
  updatedAt,
  onToggleArchive,
  onDelete,
  onCoverImageChange,
  onCreateTag,
  isArchiveToggling = false,
  isDeleting = false,
  isOwner = true, // Default to true for backward compatibility
  isCheckingOwnership = false,
}) => {
  const { updatePage } = usePageMutations();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State to track which accordion is open
  const [openAccordion, setOpenAccordion] = useState<'company' | 'project' | 'goal' | null>(null);
  const [forceOpenCompany, setForceOpenCompany] = useState(false);
  
  // Fetch project settings to check if work_order tab is enabled
  const { data: projectSettings } = useQuery({
    queryKey: ['project-settings', pageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workbook')
        .select('settings')
        .eq('id', pageId)
        .single();
      
      if (error) throw error;
      return getTabSettings(data?.settings);
    },
  });
  
  // Check for navigation state on mount
  useEffect(() => {
    if (location.state?.openCompanyInfo) {
      setForceOpenCompany(true);
      setOpenAccordion('company');
      // Clear the navigation state to prevent it from persisting
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Clear forceOpen after initial render
  useEffect(() => {
    if (forceOpenCompany) {
      const timer = setTimeout(() => {
        setForceOpenCompany(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [forceOpenCompany]);
  
  // Handler to manage accordion state
  const handleAccordionToggle = useCallback((accordionName: 'company' | 'project' | 'goal', isOpen: boolean) => {
    if (isOpen) {
      setOpenAccordion(accordionName);
    } else if (openAccordion === accordionName) {
      setOpenAccordion(null);
    }
  }, [openAccordion]);

  const handleUpdateTitle = (id: string, title: string) => {
    updatePage({ id, updates: { title } });
  };

  return (
    <SidebarLayout sidebar={<TeamMembersSidebar workbookId={pageId} />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        {/* Settings Section */}
        <DashboardCard 
          className="p-6" 
          role="region" 
          aria-labelledby="settings-section-title"
          data-section="settings"
        >
          <div id="settings-section-title" className="sr-only">Settings Section</div>
          
          {/* Page Management Options */}
          <WorkbookHeader 
            pageId={pageId}
            title={title}
            isArchived={isArchived}
            coverImage={coverImage}
            onToggleArchive={onToggleArchive}
            onDelete={onDelete}
            onCoverImageChange={onCoverImageChange}
            onCreateTag={onCreateTag}
            onUpdateTitle={handleUpdateTitle}
            isArchiveToggling={isArchiveToggling}
            isDeleting={isDeleting}
            isOwner={isOwner}
            isCheckingOwnership={isCheckingOwnership}
          />

          {/* Company Information Container */}
          <div className="mt-6">
            <CompanyInformationContainer 
              pageId={pageId} 
              isOpen={openAccordion === 'company'}
              onToggle={(isOpen) => handleAccordionToggle('company', isOpen)}
              forceOpen={forceOpenCompany}
              workOrderEnabled={projectSettings?.work_order ?? false}
            />
          </div>

          {/* Project Settings Container */}
          <div className="mt-6">
            <ProjectSettingsContainer 
              pageId={pageId}
              isOpen={openAccordion === 'project'}
              onToggle={(isOpen) => handleAccordionToggle('project', isOpen)}
            />
          </div>

          {/* Goal Settings Container */}
          <div className="mt-6">
            <GoalSettingsContainer 
              pageId={pageId}
              isOpen={openAccordion === 'goal'}
              onToggle={(isOpen) => handleAccordionToggle('goal', isOpen)}
            />
          </div>
        </DashboardCard>


      </motion.div>
    </SidebarLayout>
  );
};

// Associated Goal Component
const AssociatedGoalSection: React.FC<{ pageId: string }> = ({ pageId }) => {
  const { data: goal, isLoading, error } = useWorkbookGoal(pageId);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || !goal) {
    return (
      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <p className="text-sm text-orange-800">
          {error ? 'Error loading associated goal' : 'No associated goal found'}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900 mb-1">{goal.title}</h4>
          {goal.description && (
            <p className="text-sm text-gray-600 mb-3">{goal.description}</p>
          )}
          <div className="text-xs text-gray-500">
            Created {new Date(goal.created_at).toLocaleDateString()}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Navigate to project route
            if (goal.project_id) {
              navigate(`/project/${goal.project_id}/tasks`);
            } else {
              // Navigate to main task manager if no project ID
              navigate('/task-manager');
            }
          }}
          className="ml-3"
        >
          <ExternalLink size={14} className="mr-1" />
          View Goal
        </Button>
      </div>
    </div>
  );
};

export default WorkbookSettingsTab; 